/** Los textos de preparar el envío (fase 4): quién quedó afuera, cómo está el padrón y a dónde se sigue. */

import { num, plural } from "../../campana-nueva/utils/format";
import type { CampanaOficialDelEnvio, MotivoAfuera, MotivoAfueraMail, PadronDelEnvio } from "../types";

/**
 * El asistente del oficial con esta campaña de Activa ya elegida como audiencia (`?analisis=`). Si ya tiene borradores,
 * el asistente los muestra para seguir uno o armar otra (`useBorradorDelAnalisis`, 29/10/2026).
 */
export const rutaArmarEnOficial = (campanaActivaId: string) => `/activa/oficial/campanas/nueva?analisis=${encodeURIComponent(campanaActivaId)}`;
/** Donde se declara el consentimiento de la base en uso (el Historial de cargas de Público). */
export const RUTA_HISTORIAL_CARGAS = "/activa/publico?vista=cargas";

const MOTIVOS: [MotivoAfuera | MotivoAfueraMail, (n: number) => string][] = [
  ["control", (n) => `${num(n)} del grupo de control`],
  ["baja", (n) => `${num(n)} ${plural(n, "dado de baja", "dados de baja")}`],
  ["dijo_que_no", (n) => `${num(n)} ${plural(n, "dijo", "dijeron")} que no`],
  ["sin_si", (n) => `${num(n)} sin decir si ${plural(n, "acepta", "aceptan")}`],
  ["sin_celular", (n) => `${num(n)} sin celular`],
  ["celular_repetido", (n) => `${num(n)} con un celular repetido`],
  ["sin_mail", (n) => `${num(n)} sin mail`],
  ["mail_repetido", (n) => `${num(n)} con un mail repetido`],
  ["comparte_con_el_control", (n) => `${num(n)} que ${plural(n, "comparte", "comparten")} el celular o el mail con alguien del control`],
  ["no_enviar_snov", (n) => `${num(n)} en la lista de «No enviar» de Snov`],
  ["reboto_antes", (n) => `${num(n)} que ya ${plural(n, "rebotó", "rebotaron")} en Snov`],
];

/** «Afuera: 1 del grupo de control, 2 dijeron que no.», o "" si no quedó nadie afuera. */
export function textoAfuera(afuera: Partial<Record<MotivoAfuera | MotivoAfueraMail, number>> | null | undefined): string {
  const partes = MOTIVOS.filter(([m]) => (afuera?.[m] ?? 0) > 0).map(([m, t]) => t(afuera?.[m] ?? 0));
  return partes.length ? `Afuera: ${partes.join(", ")}.` : "";
}

/** La oferta de un grupo en palabras. */
export const textoOferta = (oferta: number) => (oferta > 0 ? `${num(oferta)}% off` : "sin descuento");

/** «En el padrón del envío: 5 por recibir, 1 enviado, 3 afuera y 2 en el control.» (sin las partes en 0, salvo el control). */
export function textoPadron(p: PadronDelEnvio): string {
  const partes = [
    p.pendiente > 0 ? `${num(p.pendiente)} por recibir` : "",
    p.enviado > 0 ? `${num(p.enviado)} ${plural(p.enviado, "enviado", "enviados")}` : "",
    p.excluido > 0 ? `${num(p.excluido)} afuera` : "",
    `${num(p.control)} en el control`,
  ].filter(Boolean);
  const lista = partes.length > 1 ? `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}` : partes[0];
  return `En el padrón del envío: ${lista}.`;
}

/** El aviso rojo: al control le llegó algo, y esas personas ya no sirven para medir. "" si no pasó. */
export function textoControlContaminado(n: number): string {
  if (n <= 0) return "";
  return `A ${num(n)} del grupo de control ${plural(n, "le", "les")} llegó un mensaje igual: ${plural(n, "esa persona ya no sirve", "esas personas ya no sirven")} para medir. Revisá la lista que se cargó en Snov.`;
}

/* ── Las campañas del WhatsApp oficial del envío: una por plantilla (R3, 29/10/2026) ───────────── */

/** Un borrador se sigue armando en el asistente (abre en el primer paso que le falta); las demás, su detalle. */
export const rutaCampanaOficial = (c: Pick<CampanaOficialDelEnvio, "id" | "estado">) =>
  `/activa/oficial/campanas/${encodeURIComponent(c.id)}${c.estado === "borrador" ? "/editar" : ""}`;

/** «Plantilla «Recuperar» · Dormidos, Campeones»; sin plantilla, que se elige al armarla. */
export const textoCampanaOficial = (c: Pick<CampanaOficialDelEnvio, "plantilla" | "grupos">) =>
  `${c.plantilla ? `Plantilla «${c.plantilla}»` : "Sin plantilla todavía: la elegís al armarla"} · ${c.grupos.map((g) => g.nombre).join(", ")}`;

/**
 * Los grupos que están en más de una campaña del oficial que no se canceló: les llegaría dos veces. No pasa si «Preparar»
 * terminó bien (cada grupo va en una sola); puede quedar si se cortó a mitad de camino, y se arregla preparando de nuevo.
 */
export function gruposRepetidos(campanas: Pick<CampanaOficialDelEnvio, "estado" | "grupos">[]): string[] {
  const veces = new Map<string, { nombre: string; n: number }>();
  for (const c of campanas.filter((x) => x.estado !== "cancelada")) {
    for (const g of c.grupos) veces.set(g.id, { nombre: g.nombre, n: (veces.get(g.id)?.n ?? 0) + 1 });
  }
  return [...veces.values()].filter((x) => x.n > 1).map((x) => x.nombre);
}
